import React, { useMemo, useState, useEffect } from "react";

function safe(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function normKey(k) {
  return String(k || "").trim();
}

function titleCase(s) {
  const x = String(s || "").trim();
  if (!x) return "";
  return x.charAt(0).toUpperCase() + x.slice(1);
}

function normalizeActionForSearch(a) {
  return String(a || "")
    .trim()
    .toLowerCase()
    .replaceAll("_", " ");
}

function actionClass(action) {
  const a = String(action || "").toLowerCase();
  if (a === "create") return "text-blue-700 font-semibold";
  if (a === "update") return "text-amber-700 font-semibold";
  if (a === "no change" || a === "no_change" || a === "nochange") return "text-green-700 font-semibold";
  if (a === "unmatched") return "text-red-700 font-semibold";
  return "text-gray-700 font-semibold";
}

function linkClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "linked") return "text-green-700 font-semibold";
  if (s === "unmatched") return "text-red-700 font-semibold";
  return "text-gray-700 font-semibold";
}

export default function BAIDImportPreviewModal({
  open,
  busy,
  error,
  preview,
  onClose,
  onConfirm,
}) {
  // ✅ Memoize rows/warnings so hook deps are stable
  const rows = useMemo(() => {
    return Array.isArray(preview?.rows_preview) ? preview.rows_preview : [];
  }, [preview?.rows_preview]);

  const warnings = useMemo(() => {
    return Array.isArray(preview?.warnings) ? preview.warnings : [];
  }, [preview?.warnings]);

  // ✅ Default to RAW view
  const [mode, setMode] = useState("raw"); // "normalized" | "raw" | "both"
  const [colSearch, setColSearch] = useState("");
  const [rowSearch, setRowSearch] = useState(""); // ✅ NEW: filter rows
  const [hideEmptyCols, setHideEmptyCols] = useState(false);

  // Reset default mode when opening
  useEffect(() => {
    if (open) {
      setMode("raw");
      setRowSearch("");
      setColSearch("");
      setHideEmptyCols(false);
    }
  }, [open]);

  // Normalized columns (explicit ordering)
  const normalizedCols = useMemo(() => {
    return [
      "rrc_number",
      "lease_name",
      "operator",
      "county",
      "well_name",
      "legal",
      "external_account_id",
      "working_interest_pct",
      "royalty_interest_pct",
      "interest_type",
      "interest_pct",
      "unit_fingerprint",
    ];
  }, []);

  // ✅ Raw columns: MUST come from backend (columns_raw) or from row.raw keys
  const rawCols = useMemo(() => {
    const fromApi = Array.isArray(preview?.columns_raw)
      ? preview.columns_raw.map(normKey).filter(Boolean)
      : [];

    if (fromApi.length) return fromApi;

    const firstRaw = rows.length ? rows[0]?.raw : null;
    if (firstRaw && typeof firstRaw === "object" && !Array.isArray(firstRaw)) {
      return Object.keys(firstRaw).map(normKey).filter(Boolean);
    }
    return [];
  }, [preview?.columns_raw, rows]);

  // Build displayed columns based on mode
  const displayedCols = useMemo(() => {
    const wantNorm = mode === "normalized" || mode === "both";
    const wantRaw = mode === "raw" || mode === "both";

    let cols = [];
    if (wantNorm) cols = cols.concat(normalizedCols.map((c) => ({ kind: "n", key: c })));
    if (wantRaw) cols = cols.concat(rawCols.map((c) => ({ kind: "r", key: c })));

    // filter by column search
    const q = (colSearch || "").trim().toLowerCase();
    if (q) cols = cols.filter((c) => c.key.toLowerCase().includes(q));

    // hide empty columns (based on previewed rows)
    if (hideEmptyCols && rows.length) {
      cols = cols.filter((c) => {
        for (const r of rows) {
          const n = r?.normalized || {};
          const raw = r?.raw || {};
          const v = c.kind === "n" ? n?.[c.key] : raw?.[c.key];
          if (v !== null && v !== undefined && String(v) !== "") return true;
        }
        return false;
      });
    }

    return cols;
  }, [mode, normalizedCols, rawCols, colSearch, hideEmptyCols, rows]);

  const est = preview?.estimated || {};

  // ✅ Summary counts (for ALL rows, not filtered)
  const derivedCounts = useMemo(() => {
    let c = 0,
      u = 0,
      n = 0,
      un = 0;

    for (const r of rows) {
      const a = String(r?.row_action || "").toLowerCase();
      if (a === "create") c++;
      else if (a === "update") u++;
      else if (a === "no_change" || a === "no change" || a === "nochange") n++;
      else if (a === "unmatched") un++;
    }
    return { c, u, n, un };
  }, [rows]);

  const statCreate = est?.would_create_rows ?? derivedCounts.c;
  const statUpdate = est?.would_update_rows ?? derivedCounts.u;
  const statNoChg = est?.would_no_change_rows ?? derivedCounts.n;

  // ✅ NEW: Filter rows by search text (e.g. type "update")
  const filteredRows = useMemo(() => {
    const q = (rowSearch || "").trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const action = r?.row_action
        ? normalizeActionForSearch(r.row_action)
        : r?.would_create_baid
        ? "create"
        : r?.would_reuse_baid
        ? "update"
        : r?.would_link
        ? "update"
        : "unmatched";

      const fp = String(r?.unit_fingerprint || "").toLowerCase();
      const idx = String(r?.row_index ?? "").toLowerCase();

      // Quick hits
      if (action.includes(q)) return true;
      if (fp.includes(q)) return true;
      if (idx.includes(q)) return true;

      // Search normalized + raw blobs (good enough for preview UX)
      const n = r?.filtered_normalized || r?.normalized || {};
      const raw = r?.raw || {};

      try {
        const blob = (JSON.stringify(n) + " " + JSON.stringify(raw)).toLowerCase();
        return blob.includes(q);
      } catch {
        return false;
      }
    });
  }, [rows, rowSearch]);

  // ✅ Now safe to conditionally render (all hooks already ran)
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/40 p-2">
      <div className="mx-auto h-[calc(100vh-16px)] w-[calc(100vw-16px)] rounded-2xl bg-white shadow-xl border border-gray-200 flex flex-col overflow-hidden">
        <div className="shrink-0 p-3 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">Preview Import (No DB Write)</div>
            <div className="text-xs text-gray-500 mt-1">
              Validate EVERYTHING that will be imported. Confirm to write.
            </div>
          </div>

          <button className="btn" onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <div className="shrink-0 p-3 space-y-3 border-b border-gray-100">
          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {String(error)}
            </div>
          ) : null}

          <div className="grid grid-cols-2 md:grid-cols-8 gap-2">
            <Stat label="Rows in file" value={safe(preview?.row_count)} />
            <Stat label="Rows previewed" value={safe(preview?.preview_count)} />
            <Stat label="Warnings" value={safe(preview?.warning_count)} />
            <Stat label="Raw cols" value={rawCols.length ? String(rawCols.length) : "—"} />

            <Stat label="Would Create" value={safe(statCreate)} />
            <Stat label="Would Update" value={safe(statUpdate)} />
            <Stat label="No Change" value={safe(statNoChg)} />

            <Stat label="Would create BAIDs" value={safe(est?.would_create_units)} />
          </div>

          {warnings.length ? (
            <details className="rounded-xl border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-900">
              <summary className="cursor-pointer font-semibold">
                View warnings (showing up to {warnings.length})
              </summary>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                {warnings.map((w, i) => (
                  <li key={i}>{String(w)}</li>
                ))}
              </ul>
            </details>
          ) : (
            <div className="rounded-xl border border-green-200 bg-green-50 p-2 text-xs text-green-900">
              No warnings in preview subset.
            </div>
          )}

          {/* Controls */}
          <div className="flex flex-col lg:flex-row lg:items-end gap-2">
            <div className="flex flex-col">
              <label className="text-[11px] text-gray-500">View</label>
              <select
                className="mt-1 rounded-lg border border-gray-200 bg-white p-2 text-sm"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={busy}
              >
                <option value="raw">Raw only (ALL Lease Summary columns)</option>
                <option value="both">Both (Normalized + Raw)</option>
                <option value="normalized">Normalized only</option>
              </select>
            </div>

            <div className="flex-1 flex flex-col">
              <label className="text-[11px] text-gray-500">
                Filter columns (type “cost”, “value”, “price”, etc.)
              </label>
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
                value={colSearch}
                onChange={(e) => setColSearch(e.target.value)}
                placeholder="e.g. CapEx, WorkingInterestValue, GasPrice"
                disabled={busy}
              />
            </div>

            {/* ✅ NEW row filter */}
            <div className="flex-1 flex flex-col">
              <label className="text-[11px] text-gray-500">
                Filter rows (try “update”, “no change”, fingerprint, lease name, etc.)
              </label>
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
                value={rowSearch}
                onChange={(e) => setRowSearch(e.target.value)}
                placeholder='Type "update" to show only updates'
                disabled={busy}
              />
            </div>

            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={hideEmptyCols}
                onChange={(e) => setHideEmptyCols(e.target.checked)}
                disabled={busy}
              />
              Hide empty columns (based on previewed rows)
            </label>

            <div className="text-xs text-gray-500">
              Showing{" "}
              <span className="font-semibold">{displayedCols.length}</span> cols ·{" "}
              <span className="font-semibold">{filteredRows.length}</span> rows
              {filteredRows.length !== rows.length ? (
                <span> (filtered from {rows.length})</span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto">
            <table className="min-w-[1800px] w-full text-xs">
              <thead className="bg-gray-50 text-gray-700 text-left sticky top-0 z-20">
                <tr>
                  <th className="py-2 px-3 whitespace-nowrap">Row</th>
                  <th className="py-2 px-3 whitespace-nowrap">Fingerprint</th>
				  <th className="py-2 px-3 whitespace-nowrap">Link</th>
				  <th className="py-2 px-3 whitespace-nowrap">Fact Action</th>

                  {displayedCols.map((c) => (
                    <th key={`${c.kind}:${c.key}`} className="py-2 px-3 whitespace-nowrap">
                      <span className="text-[11px] text-gray-500 mr-1">{c.kind === "n" ? "n:" : "raw:"}</span>
                      <span className="font-semibold">{c.key}</span>
                    </th>
                  ))}

                  <th className="py-2 px-3 whitespace-nowrap">Row warnings</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((r) => {
                  const n = r?.normalized || {};
                  const raw = r?.raw || {};
                  const action = r?.row_action
                    ? titleCase(String(r.row_action).replaceAll("_", " "))
                    : r?.would_create_baid
                    ? "Create"
                    : r?.would_reuse_baid
                    ? "Update"
                    : r?.would_link
                    ? "Update"
                    : "Unmatched";
					
				  const linkStatus = r?.would_link ? "Linked" : "Unmatched";	

                  const warn = Array.isArray(r?.warnings) ? r.warnings : [];

                  return (
                    <tr key={String(r?.row_index)} className="border-t">
                      <td className="py-2 px-3 font-mono">{safe(r?.row_index)}</td>
                      <td className="py-2 px-3 font-mono">{safe(r?.unit_fingerprint)}</td>
                      <td className="py-2 px-3">
					    <span className={linkClass(linkStatus)}>{linkStatus}</span>
					  </td>

					  <td className="py-2 px-3">
					    <span className={actionClass(action)}>{action}</span>
					  </td>

                      {displayedCols.map((c) => {
                        const v = c.kind === "n" ? n?.[c.key] : raw?.[c.key];
                        return (
                          <td
                            key={`${c.kind}:${c.key}`}
                            className="py-2 px-3 whitespace-nowrap"
                            title={safe(v)}
                          >
                            {safe(v) || "—"}
                          </td>
                        );
                      })}

                      <td className="py-2 px-3">{warn.length ? warn.join(" | ") : "—"}</td>
                    </tr>
                  );
                })}

                {!filteredRows.length ? (
                  <tr>
                    <td className="py-6 px-3 text-gray-500" colSpan={4 + displayedCols.length + 1}>
                      No rows match the current row filter.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="shrink-0 p-3 border-t border-gray-100 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-500">
            Confirm will write the full file to DB using the same settings as preview.
          </div>

          <button className="btn" disabled={busy || !preview} onClick={onConfirm}>
            Confirm Import →
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-2">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-900">{value || "—"}</div>
    </div>
  );
}
